import { PayloadAction } from '@reduxjs/toolkit';
import { all, fork, call, put, select, takeLatest, take, delay } from 'redux-saga/effects';
import { IUpdateState, restoreState, updateState } from '../../../actions/product/state';
import { EntityTypes } from 'constants/entities';
import { AssetType, IAsset } from 'types/app/asset';
import { selectActionAssets } from 'core/selectors/product/action/actionAssets';
import { LoadingState } from 'types/domain/base';
import { loadAsset } from 'core/services/asset';
import { selectSceneAsset } from 'core/selectors/product/scene/sceneAsset';
import { setAssets, unloadAsset } from 'core/slices/assets';
import { IKeyArray } from 'types/IKeyArray';
import { selectFeedNext } from 'core/selectors/product/action/actionNext';
import { IActionFeedItem } from 'core/selectors/product/action/actionFeed';
import { RootState } from 'types';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { Mode } from 'core/slices/session';
import { loadingState } from 'core/slices/player';
import { selectAction } from 'core/selectors/product/action/action';
import { ActionType, IActionBasic } from 'types/domain/action';
import { IStateReducer } from 'core/reducers/state';

import { waitForPageTransition } from '../progress/page';
import { SceneType } from 'types/domain/scene';

export interface ILoader {
  [assetId: string]: any;
}

export const loaders = {};

function* setAssetsSaga(action: PayloadAction<IKeyArray<IAsset>>) {
  const state: IStateReducer = yield select((state: RootState) => state.state);

  if (state[EntityTypes.SCENE]) yield loadSceneAsset(state[EntityTypes.SCENE], true);
}

function* assetLoadedSaga(action: any) {
  const { id } = action.meta.arg;
  const sceneAsset = yield select(state => selectSceneAsset(state, undefined));
  const loading = select((state: RootState) => state.core.player.loading.scene === LoadingState.LOADING);

  if (sceneAsset && loading && sceneAsset.id === id && sceneAsset.type !== AssetType.VIDEO)
    yield put(yield call(loadingState, { scene: LoadingState.LOADED }));
}

function* assetFailedSaga(action: any) {
  const asset: IAsset = yield select((state: RootState) => state.core.assets[action.meta.arg.id]);

  if (asset.attempts < 5) {
    yield delay(100);
    yield put(yield call(loadAsset, asset));
  }
}

function* restoreStateSaga(action: PayloadAction<IUpdateState[]>) {
  const update = action.payload.find(update => update.type === EntityTypes.ACTION);

  if (update) yield loadActionAssets();
}

function* updateStateSaga(action: PayloadAction<Partial<IUpdateState>>) {
  switch (action.payload.type) {
    case EntityTypes.ACTION:
      yield loadActionAssets();
      break;
    case EntityTypes.SCENE:
      //  const mode: Mode = yield select(selectSessionMode);
      // if (mode === Mode.ADMIN)
      yield loadSceneAsset(action.payload.id, true);
      break;
    // case EntityTypes.VERIFICATION_REQUEST:
    //   const mode: Mode = yield select(selectSessionMode);
    //   const next: IActionFeedItem = yield select(selectFeedNext);

    //   const nextAction: IActionBasic = yield select((state: RootState) =>
    //     //@ts-ignore
    //     next?.actionId ? selectAction(state, next.actionId) : null,
    //   );
    //   if (next && nextAction?.scene && mode === Mode.LEARNING) yield loadSceneAsset(nextAction.sceneId);
    //   break;
  }
}

function* loadActionAssets() {
  const mode: Mode = yield select(selectSessionMode);
  const nextItem: IActionFeedItem = yield select(selectFeedNext);

  const nextAction: IActionBasic = yield select((state: RootState) =>
    //@ts-ignore
    nextItem?.actionId ? selectAction(state, nextItem.actionId) : null,
  );

  const action: IActionBasic = yield select(state => selectAction(state, undefined));

  if (action?.voiceSceneId) {
    yield loadSceneAsset(action.voiceSceneId);
  }

  const actionAssets: IAsset[] = yield select(selectActionAssets);
  const filtered = actionAssets.filter(asset => ![LoadingState.LOADED, LoadingState.LOADING].includes(asset.loading));

  const actions = yield all(filtered.map(asset => call(loadAsset, asset)));
  yield all(actions.map(put));

  if (!nextAction) return;

  if (![ActionType.INTERACTIVE].includes(nextAction.type) && nextAction.scene?.type === SceneType.PHOTO) return;

  if (mode !== Mode.ADMIN) {
    if (nextAction.scene) yield loadSceneAsset(nextAction.sceneId);
    if (nextAction.voiceSceneId) yield loadSceneAsset(nextAction.voiceSceneId);
  }
}

export function* loadSceneAsset(sceneId: number, current?: boolean) {
  // console.log('loadSceneAsset', sceneId, current);

  yield waitForPageTransition();

  //@ts-ignore
  let sceneAsset: IAsset = yield select((state: RootState) => selectSceneAsset(state, sceneId));

  while (!sceneAsset) {
    yield take(setAssets.type);
    //@ts-ignore
    sceneAsset = yield select((state: RootState) => selectSceneAsset(state, sceneId));
  }

  if (sceneAsset && ![LoadingState.LOADED, LoadingState.LOADING].includes(sceneAsset.loading)) {
    yield put(yield call(loadAsset, sceneAsset));

    if (current) {
      yield put(yield call(loadingState, { scene: LoadingState.LOADING }));
    }
  } else if (sceneAsset.type !== AssetType.VIDEO && sceneAsset.loading === LoadingState.LOADED) {
    yield put(yield call(loadingState, { scene: LoadingState.LOADED }));
  }
}

function* unloadAssetSaga(action: PayloadAction<string>) {
  const asset: IAsset = yield select((state: RootState) => state.core.assets[action.payload]);

  const loader = loaders[asset.id];
  if (!loader) return;

  switch (asset.type) {
    case AssetType.VIDEO:
      if (loader.isReady && loader.isReady()) {
        yield call([loader, loader.pause]);
        yield call([loader, loader.destroy]);
      }
      break;
    case AssetType.IMAGE:
      if (loader.asset && loader.asset.dispose) {
        yield call([loader.asset, loader.asset.dispose]);
      }
      break;
  }

  delete loaders[asset.id];
}

function* watchAssetLoaded() {
  yield takeLatest(loadAsset.fulfilled, assetLoadedSaga);
}

function* watchAssetFailed() {
  yield takeLatest(loadAsset.rejected, assetFailedSaga);
}

function* watchSetAssets() {
  yield takeLatest(setAssets.type, setAssetsSaga);
}

function* watchRestoreState() {
  yield takeLatest(restoreState.type, restoreStateSaga);
}

function* watchUpdateState() {
  yield takeLatest(updateState.type, updateStateSaga);
}

function* watchUnloadAsset() {
  yield takeLatest(unloadAsset, unloadAssetSaga);
}

export function* loadSaga() {
  yield all([
    fork(watchUpdateState),
    fork(watchRestoreState),
    fork(watchSetAssets),
    fork(watchAssetLoaded),
    fork(watchUnloadAsset),
    fork(watchAssetFailed),
  ]);
}
